import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { v4 } from 'uuid';

import { DB } from '~/services/firebase';
import { useAuth } from '~/hooks/auth';

import Dashboard from '~/layouts/Dashboard';
import getValidationErrors from '~/utils/getValidationErrors';
import { useToast } from '~/hooks/toast';
import { useCategoriesCompany } from '~/hooks/categoriesCompany';
import { bff } from '~/services/bff';
import { useProductsCompany } from '~/hooks/productsCompany';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';
import IProduct from '~/interfaces/IProduct';
import { useMenus } from '~/hooks/menus';

import { weekDays } from '~/utils/getWeekDays';

interface FormData {
  uid: string;
  name: string;
  initHour: string;
  endHour: string;
  status: boolean;
  weekDays: string;
}

interface Params {
  id: string;
}

type GroupedProductType = {
  [key: string]: IProduct[];
};

const { REACT_APP_FIREBASE_PROJECT } = process.env;

const placeholder =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAARMAAAC3CAMAAAAGjUrGAAAAMFBMVEXx8/XCy9K/yND09vfw8vTP1tzp7O/i5ure4+fO1dvJ0dfT2d/EzNPt7/Lb4OXo6+4FeM7UAAAFL0lEQVR4nO2c24KrIAxFLdha7///t0dxOlWDSiAKztnrbR4G6SoJBKHZA6zJYncgQeCEAicUOKHACQVOKHBCgRMKnFDghAInFDihwAkFTihwQoETCpxQ4IQCJxQ4ocAJBU4ocEKBEwqcUOCEAicUOKHACQVOKHBCgRMKnFDghAInFDihwAkFTihwQoETCpxQ4IQCJxQ4ocAJBU4ot3Oi1KMq64FnWTVq+EueWzlRquqKVn/J+/ezEfdyHydKPYtc62yF1m1Xymq5ixPVdDnx8eslf1eCVu7hRFXFppAfLW39kNJyByeqOTJirGTvRsbKDZyozsHIpKUQsZK8E1Vu55GTrKTuRL0ZRoyVLviZaTtRVctUMuaVOnCoJO1E1WwjxsorbGZO2Qk7br5WuhApKTvpfZWMy5WAoZKuk6b1NhI4VJJ10uRBSsas0ng+OlUnVaARw9NvqCTqRERJpt9eUtJ0IqPEN36SdNIIKRnIPeafFJ0Ep9c5mr+qTdFJ2CRMpLAn5fScqJeokrFWZkoRdaImwtpw2T9iSnnxuiDoRFXda6hK28JzWTA14ryBxKFlTT9iTlT1W57o3Lta96yED8krRieknCw/DDuEP1TnKBlgzMlCTtZDXr+8pIjOwitK5x7JOKFD3mukiE85ix45S5FxYll46prdiv8ekpsU19wv4kS9LV1ouQPlrPzKliIzTuw9YDYiVfgFSxFx8rR+wcyMomSX9HYpTjlFwonqrB3gBc/JyYQjRcRJYe8Ay4l9rMlLcVi8iTjp7Y/nOBHcMjngWEoi4+TUlcmKw9rnxHzCWMqeU/ltkB9JEZl3SusnYmwQn1fm2GgPeiOzZrM9WZfu/3/BNDznYATLOLENffep+JppeMZBMSZUF9N6ljFM7KF3qpTduBZyQj4W53XTiRsEm1L2dr2k9k9W9Rtjq2BrJj9Zyk7pI7bP9lw8kfH+4KIFLGF77Sa3R90Un0POvHNCcYzsLVMk9+2buni1bd9xjMSJHMPmjCz7zov/fidW5GQ7OS/2e8BoRrLtrBfXScTIMVLsk09cJxEjZ8I6+cR1EmG1tsRaDsZ0EjlyDL0leuxOpulD4JTALtfXORRbnqVO1LDOePdtpoclWPsqulL+wt0P0SNnxFKrrp2opmuXl+5OuHA3PSmByDGQ9ezSydYdM+ELd4YUIsdANnoWTva2RSUv3JlnJRE5I2RbY+6kee1+dTrrhC7cPTZeMUdivZnydaIc3tdqqWuI6USOYZlSfp0oxzVlJxNByUSOYZlSPk6cDzqEXy17JDTn/LBMKRlTSRZ4X2giep2zZnEwZHLiGjifFt6BTtKKHMMspUxO2BkvDzoDm1jkGGa7bsaJx0t9XfgrOfuMlhezwsc48RrKufvhyiXXHatg8T2Zkm0eHzluxO8W4pXHKljkXycBt3h9blFdeqyCx2fPOguLbn6qTWsBu+Czxs/CopsdP4kmkx+mcZ8FRrfuWUqSTSYT005keDucW4iXnzRhMg17iYacC6A0VyZzzIQs0pBrUrn22JoXY4Us0pDjaZMzb+dIMX6/Qi0dHSU0XHySz48heqSaOs60vsvlq2mtpzj9OCh/Trgjew7afgLar63d6ec2SmTZm37+UyV7048K+Gmkm7O10A/8aaSbY7sEr8rYvYoNnX4Sr3EuYJVpVc35Ccu/innZbryMJ1n4v9f4N9FZ39XPZ931GYzMGH9VPHYfAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAADp8Q9+nG9anuOrfAAAAABJRU5ErkJggg==';

const MenuForm: React.FC = () => {
  const history = useHistory();
  const params = useParams<Params>();
  const { state } = useLocation();
  const { user, company } = useAuth();
  const { addToast } = useToast();

  const { products, isInitialized, loadProducts } = useProductsCompany();
  const { loadMenus } = useMenus();

  const [weekDaysSelected, setWeekDaysSelected] = useState([] as number[]);

  const [data, setData] = useState({} as FormData);
  const [errors, setErrors] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const [showAccordion, setShowAccordion] = useState('');
  const [productIds, setProductIds] = useState<string[]>([]);
  const { id } = params;

  const toggleWeekDay = useCallback(
    (value: number) => {
      let items = [];

      if (weekDaysSelected.includes(value)) {
        items = weekDaysSelected.filter(item => item !== value);
      } else {
        items = [...weekDaysSelected, value];
      }

      setWeekDaysSelected(items);
    },
    [weekDaysSelected],
  );

  useEffect(() => {
    if (!isInitialized) loadProducts();
  }, [products, loadProducts, isInitialized]);

  const groupedProducts = useMemo(() => {
    const group = {} as GroupedProductType;
    const auxGroup = [] as string[];

    products.forEach(item => {
      if (auxGroup.includes(item.category.uid)) {
        group[item.category.uid].push(item);
      } else {
        auxGroup.push(item.category.uid);
        group[item.category.uid] = [item];
      }
    });

    return group;
  }, [products]);

  const selectProduct = useCallback(
    (productId: string) => {
      const ids = [...productIds];
      if (ids.includes(productId)) {
        const rest = ids.filter(item => item !== productId);
        setProductIds(rest);
      } else {
        ids.push(productId);
        setProductIds(ids);
      }
    },
    [productIds],
  );

  const selectAll = useCallback(
    (category_id: string) => {
      const ids = [...productIds];
      groupedProducts[category_id].forEach(item => {
        if (!ids.includes(item.uid)) ids.push(item.uid);
      });
      setProductIds(ids);
    },
    [groupedProducts, productIds],
  );

  const unselectAll = useCallback(
    (category_id: string) => {
      const ids = [...productIds];

      const removeIds = groupedProducts[category_id].map(item => item.uid);
      const filter = ids.filter(item => !removeIds.includes(item));

      setProductIds(filter);
    },
    [groupedProducts, productIds],
  );

  useEffect(() => {
    async function loadMenu() {
      const categoriesRef = doc(DB, 'menus', id);
      const docSnap = await getDoc(categoriesRef);
      const menuData = docSnap.data() as any;

      setData({
        ...menuData,
        uid: id,
      } as FormData);
      setProductIds(menuData.productIds);
      setWeekDaysSelected(
        menuData.weekDays.split(',').map((item: string) => Number(item)),
      );
    }

    if (state) {
      setData(state as FormData);
      setProductIds((state as any).productIds);
      setWeekDaysSelected(
        (state as any).weekDays.split(',').map((item: string) => Number(item)),
      );
    } else if (id) {
      loadMenu();
    } else {
      setData({ status: true } as FormData);
      setProductIds([]);
      setWeekDaysSelected([0, 1, 2, 3, 4, 5, 6]);
    }
  }, [id, state]);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        const schema = Yup.object().shape({
          name: Yup.string().required('O nome é obrigatório'),
          initHour: Yup.string().required('O horário é obrigatório'),
          endHour: Yup.string().required('O horário é obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const uid = data.uid || v4();

        const parseData = {
          ...data,
          uid,
          productIds,
          user_id: user.uid,
          company_id: company.uid,
          weekDays: weekDaysSelected.join(','),
        };

        if (data.uid) {
          await updateDoc(doc(DB, 'menus', uid), parseData);
        } else {
          await setDoc(doc(DB, 'menus', uid), parseData);
        }

        await bff.delete(`/menus/${REACT_APP_FIREBASE_PROJECT}`);
        await loadMenus(company.uid);

        addToast({
          type: 'success',
          description: data.uid
            ? 'Cardápio alterado com sucesso!'
            : 'Cardápio criado com sucesso!',
        });

        history.push('/menus');

        setLoading(false);
      } catch (err: any) {
        console.log(err);
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }
      }
    },
    [
      user,
      company,
      data,
      addToast,
      history,
      productIds,
      loadMenus,
      weekDaysSelected,
    ],
  );

  return (
    <Dashboard containerMessage="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
      <div className="row">
        <div className="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
          <div className="nk-block nk-block-lg">
            <div className="nk-block-head">
              <div className="nk-block-head-content">
                <div className="nk-block-head-sub">
                  <Link className="back-to" to="/menus">
                    <em className="icon ni ni-arrow-left" />
                    <span>Voltar</span>
                  </Link>
                </div>
                <h4 className="title nk-block-title">
                  {id ? 'Editar Cardápio' : 'Criar Cardápio'}
                </h4>
                <div className="nk-block-des">
                  <p>Preencha o formulário abaixo e crie um novo cardápio.</p>
                </div>
              </div>
            </div>
            <div className="card card-bordered">
              <div className="card-inner">
                <form onSubmit={handleSubmit}>
                  <div className="row g-2">
                    <div className="col-lg-4">
                      <div className="form-group">
                        <label className="form-label">Nome do Cardápio</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.name}
                            onChange={event => {
                              setData({ ...data, name: event.target.value });
                            }}
                          />
                          {errors.name && (
                            <span className="invalid">{errors.name}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-3">
                      <div className="form-group">
                        <label className="form-label">Hora Início</label>
                        <div className="form-control-wrap">
                          <input
                            type="time"
                            className="form-control"
                            defaultValue={data.initHour}
                            onChange={event => {
                              setData({
                                ...data,
                                initHour: event.target.value,
                              });
                            }}
                          />
                          {errors.initHour && (
                            <span className="invalid">{errors.initHour}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-3">
                      <div className="form-group">
                        <label className="form-label">Término</label>
                        <div className="form-control-wrap">
                          <input
                            type="time"
                            className="form-control"
                            defaultValue={data.endHour}
                            onChange={event => {
                              setData({ ...data, endHour: event.target.value });
                            }}
                          />
                          {errors.endHour && (
                            <span className="invalid">{errors.endHour}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-2">
                      <div className="form-group">
                        <label className="form-label">Status?</label>
                        <ul className="custom-control-group g-3 align-center">
                          <li>
                            <div className="custom-control custom-control-sm custom-checkbox">
                              <input
                                id="active"
                                type="checkbox"
                                className="custom-control-input"
                                defaultChecked={data.status}
                                onClick={() => {
                                  setData({
                                    ...data,
                                    status: !data.status,
                                  });
                                }}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="active"
                              >
                                Ativo
                              </label>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="col-lg-12">
                      <div className="form-group">
                        <label className="form-label">
                          Em quais dias da semana o cardápio irá funcionar?
                        </label>
                        <ul className="custom-control-group g-3 align-center">
                          <li>
                            {weekDays.map(item => (
                              <div
                                key={item.name}
                                className="custom-control custom-control-sm custom-checkbox mr-2"
                              >
                                <input
                                  id={`${item.id}-${item.name}`}
                                  type="checkbox"
                                  className="custom-control-input"
                                  checked={weekDaysSelected.includes(item.id)}
                                  onClick={() => toggleWeekDay(item.id)}
                                />
                                <label
                                  className="custom-control-label"
                                  htmlFor={`${item.id}-${item.name}`}
                                >
                                  {item.name}
                                </label>
                              </div>
                            ))}
                          </li>
                        </ul>
                      </div>
                    </div>

                    <label className="form-label mt-4 mb-0">
                      Selecione os produtos do cardápio:
                    </label>

                    <div className="accordion border-0">
                      {Object.entries(groupedProducts).map(([key, value]) => (
                        <div className="accordion-item" key={key}>
                          <div
                            onClick={() => {
                              setShowAccordion(key);
                            }}
                            className={`accordion-head bg-gray-100 ${
                              showAccordion === key ? '' : 'collapsed'
                            }`}
                            style={{ cursor: 'pointer' }}
                          >
                            <h6 className="title">{value[0].category.name}</h6>
                            <span className="accordion-icon" />
                          </div>
                          <div
                            className={`accordion-body collapse ${
                              showAccordion === key ? 'show' : ''
                            }`}
                          >
                            <div
                              className="accordion-inner border-0 p-0"
                              style={{ maxHeight: 300, overflow: 'auto' }}
                            >
                              <div className="nk-tb-list">
                                <button
                                  type="button"
                                  onClick={() => selectAll(key)}
                                >
                                  marcar todos
                                </button>
                                <button
                                  type="button"
                                  className="ml-2"
                                  onClick={() => unselectAll(key)}
                                >
                                  desmarcar todos
                                </button>
                                {value.map((product: IProduct) => (
                                  <div
                                    className="nk-tb-item"
                                    key={product.uid}
                                    style={{ width: '100%', cursor: 'pointer' }}
                                    onClick={() => selectProduct(product.uid)}
                                  >
                                    <div className="nk-tb-col">
                                      <span className="tb-product">
                                        <input
                                          readOnly
                                          type="checkbox"
                                          checked={productIds.includes(
                                            product.uid,
                                          )}
                                        />
                                        <img
                                          src={
                                            product.image
                                              ? product.image
                                              : placeholder
                                          }
                                          alt="categoria"
                                          className="thumb ml-2"
                                          style={{ maxWidth: 80 }}
                                        />
                                        <span className="title">
                                          {product.name}
                                        </span>
                                      </span>
                                    </div>

                                    <div className="nk-tb-col">
                                      <div className="asterisk tb-asterisk">
                                        <span className="fs-14px">
                                          {getCurrencyFormatted(
                                            parseFloat(
                                              product.enable_promotion
                                                ? product.value_promotion
                                                : product.value,
                                            ),
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <button
                          type="submit"
                          className="btn btn-lg btn-primary"
                          disabled={loading}
                        >
                          {loading && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          )}
                          {!loading && 'Salvar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default MenuForm;
